import React, { useState, useEffect, useRef } from "react";
import { PlusCircle, Trash, Trash2, X } from "lucide-react";
import {
  FOREX_CARD_CURRENCY_OPTIONS,
  FOREX_CURRENCIES_NOTES_OPTIONS,
} from "pages/GenericOrder/steps/product-modal";
import { addCurrency, AddCurrencyParams } from "pages/GenericOrder/apis";
import { useMutation, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
// Same signature, answered by the new backend. See legacy-order-api.ts.
import { getLiveRates } from "service/moneeflo/legacy-order-api";

interface AddCurrencyDialogProps {
  setIsCurrencyDialogOpen: (open: boolean) => void;
  prodcutType: string;
  selectedProductType: string;
  customers_id: number;
  ProdcutList: any;
}

interface CurrencyItem {
  id: number;
  currency: string;
  amount: string;
  rate: number;
  total: number;
}

const AddCurrencyDialog: React.FC<AddCurrencyDialogProps> = ({
  setIsCurrencyDialogOpen,
  prodcutType,
  selectedProductType,
  customers_id,
  ProdcutList,
}) => {

  const queryClient = useQueryClient();
  const order_details_id = Number(useParams().id);
  const [rows, setRows] = useState<CurrencyItem[]>([]);
  const isInitialized = useRef(false);

  const finalCurrencyListOptions =
    selectedProductType === "Card"
      ? FOREX_CARD_CURRENCY_OPTIONS
      : FOREX_CURRENCIES_NOTES_OPTIONS;
  const finalCurrencyList = finalCurrencyListOptions.filter((currency) =>
    ProdcutList?.every((prod: any) => prod?.currencyWant !== currency.value)
  );

  // Fetch live rate for a given currency
  const fetchRate = async (currency: string) => {
    try {
      const res = await getLiveRates({
        currency: currency.toUpperCase(),
        type: prodcutType,
        fx_product: selectedProductType,
      });
      return Number(res?.rates) || 0;
    } catch (err) {
      toast.error("Failed to fetch rate");
      return 0;
    }
  };

  // Add a new currency row
  const handleAddRow = async (defaultCurrency?: string) => {
    let currency = defaultCurrency;

    if (!currency) {
      const availableCurrency = finalCurrencyList.find(
        (curr) => !rows.some((row) => row.currency === curr.value)
      );

      if (!availableCurrency) {
        toast.error("All available currencies have been added");
        return;
      }

      currency = availableCurrency.value;
    }

    const currencyExists = rows.some((row) => row.currency === currency);
    if (currencyExists) {
      toast.error(`${currency} is already added`);
      return;
    }

    const rate = await fetchRate(currency);

    setRows((prev) => [
      ...prev,
      { id: Date.now(), currency, amount: "", rate, total: 0 },
    ]);
  };

  // Update row when currency changes
  const handleCurrencyChange = async (id: number, newCurrency: string) => {
    const currencyExists = rows.some(
      (row) => row.id !== id && row.currency === newCurrency
    );
    if (currencyExists) {
      toast.error(`${newCurrency} is already added`);
      return;
    }

    const rate = await fetchRate(newCurrency);
    setRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
            ...row,
            currency: newCurrency,
            rate,
            total: parseFloat(
              ((parseFloat(row.amount) || 0) * rate).toFixed(4)
            ),
          }
          : row
      )
    );
  };

  // Update row when amount changes
  const handleAmountChange = (id: number, newAmount: string) => {
    setRows((prev) =>
      prev.map((row) =>
        row.id === id
          ? {
            ...row,
            amount: newAmount,
            total: parseFloat(
              ((parseFloat(newAmount) || 0) * row.rate).toFixed(4)
            ),
          }
          : row
      )
    );
  };

  // Remove row
  const handleRemoveRow = (id: number) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
  };

  const canSave =
    rows.length > 0 &&
    rows.every((r) => parseFloat(r.amount) > 0 && r.rate > 0);

  // Mutation to save
  const mutation = useMutation({
    mutationFn: async () => {
      const productPayload: AddCurrencyParams[] = rows.map((row) => ({
        currency_have: "INR",
        currency_want: row.currency,
        city_country: "",
        product_id: selectedProductType === "Card" ? 16 : 9,
        fx_amount: parseFloat(row.amount),
        inr_amount: row.total,
        rate: row.rate,
        customers_id,
      }));
      const channels_id = Number(import.meta.env.VITE_X_FOREX_ORDER_CHANNEL_ID);
      const branch = Number(localStorage.getItem("branch"));

      // ✅ Check duplicates
      const duplicate = productPayload?.find((payload) =>
        ProdcutList?.some(
          (prod: any) =>
            prod?.currencyWant === payload?.currency_want &&
            prod?.currencyHave === payload?.currency_have &&
            prod?.product_id === payload?.product_id
        )
      );

      if (duplicate) {
        toast.error(
          `Currency ${duplicate?.currency_want} already exists in Product List`
        );
        throw new Error("Duplicate currency found");
      }

      return await addCurrency({
        order_details_id,
        channels_id,
        branch,
        product: productPayload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["orderDetails"]);
      setIsCurrencyDialogOpen(false);
    },
    onError: (err) => {
      console.error("Failed to save", err);
      if (err.message !== "Duplicate currency found") {
        toast.error("Failed to save currencies");
      }
    },
  });

  useEffect(() => {
    if (!isInitialized.current && rows.length === 0) {
      isInitialized.current = true;
      const firstCurrency = finalCurrencyList[0]?.value || "USD";
      handleAddRow(firstCurrency);
    }
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black-900 bg-opacity-60 animate-overlayShow">
      <div
        className="relative bg-white-A700 rounded-[12px] animate-contentShow overflow-hidden"
        style={{ width: "582px", boxShadow: "0px 4px 4px 0px #00000040" }}
      >
        <div className="p-6">
          <button
            onClick={() => setIsCurrencyDialogOpen(false)}
            className="absolute top-4 right-4 text-[#666] hover:text-black z-10"
          >
            <X size={20} />
          </button>

          {/* Header */}
          <div className="mb-4">
            <h2 className="text-lg font-semibold mb-1">
              Add {selectedProductType}
            </h2>
            <span className="text-[#7E7E7E] text-sm font-medium">
              Fill the required details
            </span>
            <div className="mt-4 border-t border-[#D2D2D2]" />
          </div>

          {/* Dynamic Rows */}
          {rows?.map((row) => (
            <div key={row?.id} className="mb-3 max-w-sm">

              <div className="flex items-center gap-2">
                <div className="flex items-center border rounded-[8px] px-[11px] py-1 w-full">
                  <select
                    value={row?.currency}
                    onChange={(e) => handleCurrencyChange(row?.id, e.target.value)}
                    className="w-[100px] bg-[#E1EBFC] border-[#E1EBFC] rounded-[6px] text-[#165FE3] p-2.5 text-sm"
                  >
                    {finalCurrencyList.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="number"
                    value={row?.amount}
                    onChange={(e) => handleAmountChange(row?.id, e.target.value)}
                    className="flex-1 px-3 py-2 border-none outline-none font-medium ml-3 min-w-0 text-[#333333] text-sm"
                    placeholder="Enter amount"
                  />

                  <div className="flex items-center gap-2 text-sm font-medium text-[#333] ml-3">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    INR {row?.rate}
                  </div>
                </div>
                <div className="">
                  <Trash2 className="w-5 h-5" color='red' onClick={() => handleRemoveRow(row.id)} />
                </div>
              </div>

              {row?.amount && (
                <div className="text-sm text-[#666] ml-2 mt-1">
                  {row?.currency} {row?.amount} × INR {row?.rate} ={" "}
                  <span className="font-medium">INR {row?.total}</span>
                </div>
              )}
            </div>
          ))}

          {/* Add new row button */}
          <button
            onClick={() => handleAddRow()}
            className="flex items-center gap-1 text-sm font-medium mt-2 text-[#165FE3]"
            disabled={rows?.length >= finalCurrencyList?.length}
          >
            <PlusCircle size={16} /> Add Currency
          </button>

          {rows?.length >= finalCurrencyList?.length && (
            <p className="text-sm text-[#666] mt-1 ml-5">
              All available currencies have been added
            </p>
          )}
        </div>

        <div className="flex justify-start gap-3 bg-[#F9F9F9] py-5 px-6 mt-6">
          <button
            onClick={() => setIsCurrencyDialogOpen(false)}
            className="px-4 py-[11px] text-[#165FE3] hover:bg-gray-100 rounded-[8px] border border-[#165FE3]"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!canSave}
            className={`px-4 py-[11px] rounded-[8px] ${canSave
              ? "bg-[#165FE3] text-[#fff] hover:bg-blue-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCurrencyDialog;
